// 시장 이슈의 문서 탭 데이터 — 리포트·IR·공시·특허.
//
// 토픽 탭은 정시 스냅샷 하나를 받아 클라이언트에서 걸러 쓰지만, 문서 탭은 건수가
// 수백만이라 서버 페이지네이션을 그대로 쓴다(리포트 114만 · 공시 297만 · 특허 337만).
// 그래서 화면 상태(로딩·에러·더 받기)를 여기서 관리하고 화면은 그리기만 한다.
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

export interface MkDocCompany {
  symbol: string;
  name: string;
  exchange?: string;
  /** 문서 sentiment 를 옮긴 것 — 긍정·부정·중립. 없으면 빈 문자열(배지 없음) */
  rel?: string;
}

export interface MkDocInsight {
  summary: string;
  keyPoints: string[];
}

export interface MkDoc {
  id: string;
  title: string;
  /** 발행 주체 — 증권사·회사·출원인 */
  pub: string;
  author: string;
  date: string;
  time: string;
  url: string;
  summary: string;
  thumb: string;
  companies: MkDocCompany[];
  /** 리포트에만 붙는 AI 요약 */
  insight?: MkDocInsight;
}

interface DocsResponse {
  type: string;
  label: string;
  query: string;
  page: number;
  /** 다음 페이지가 있는지. 총건수·총페이지는 서버가 내리지 않는다 — 코퍼스
   *  규모를 드러내면 전량 수집을 부추긴다. */
  hasMore: boolean;
  items: MkDoc[];
}

/** 서버가 필드를 늘려도 화면이 깨지지 않게 한 곳에서 방어한다(토픽 탭과 같은 이유). */
function normalizeDoc(d: Partial<MkDoc> | null | undefined): MkDoc {
  const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  const ins = d?.insight;
  return {
    id: String(d?.id || ""),
    title: String(d?.title || ""),
    pub: String(d?.pub || ""),
    author: String(d?.author || ""),
    date: String(d?.date || ""),
    time: String(d?.time || ""),
    url: String(d?.url || ""),
    summary: String(d?.summary || ""),
    thumb: String(d?.thumb || ""),
    companies: arr<MkDocCompany>(d?.companies),
    insight: ins && (ins.summary || (ins.keyPoints || []).length)
      ? { summary: String(ins.summary || ""), keyPoints: arr<string>(ins.keyPoints) }
      : undefined,
  };
}

export interface DocsState {
  items: MkDoc[];
  loading: boolean;
  error: string;
  hasMore: boolean;
  loadMore: () => void;
}

/** 문서 목록 — 종류·검색어가 바뀌면 처음부터 다시 받는다.
 *
 *  검색어는 호출측에서 이미 디바운스된 값을 준다고 본다(입력마다 조회하지 않게).
 *  요청이 겹칠 수 있어(빠른 탭 전환) 마지막 요청만 반영한다 — seq 로 구분한다.
 */
export function useDocs(type: string, query: string): DocsState {
  const [items, setItems] = useState<MkDoc[]>([]);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const seq = useRef(0);

  const load = useCallback(async (p: number, append: boolean) => {
    // 토픽 탭은 이 훅을 쓰지 않는다 — 호출측 분기를 단순하게 두려고 여기서 막는다
    if (!type || type === "topic") return;
    const mine = ++seq.current;
    setLoading(true);
    setError("");
    try {
      const d = await api<{ ok: boolean; data?: DocsResponse; message?: string }>(
        `/agent/market/docs?type=${encodeURIComponent(type)}`
        + `&page=${p}${query ? `&q=${encodeURIComponent(query)}` : ""}`,
      );
      if (mine !== seq.current) return;      // 더 최근 요청이 있다 — 이 결과는 버린다
      if (!d.ok || !d.data) throw new Error(d.message || "문서를 불러오지 못했습니다.");
      const got = d.data.items.map(normalizeDoc);
      setItems((prev) => (append ? [...prev, ...got] : got));
      setMore(!!d.data.hasMore);
      setPage(d.data.page);
    } catch (e) {
      if (mine !== seq.current) return;
      setError(e instanceof Error ? e.message : "문서를 불러오지 못했습니다.");
      if (!append) setItems([]);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [type, query]);

  useEffect(() => {
    setItems([]);
    setMore(false);
    setPage(1);
    void load(1, false);
  }, [load]);

  const hasMore = more;
  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    void load(page + 1, true);
  }, [loading, hasMore, page, load]);

  return { items, loading, error, hasMore, loadMore };
}

/** 입력을 잠깐 묶어 준다 — 글자마다 조회하면 수백만 건 검색이 계속 나간다. */
export function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}
